import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { ClickableSvgIcon, Drawer, TreeView, TreeItem, Label } from 'irmlib/components/Controls'
import { XDivider, useTheme, makeStyles } from 'irmlib/styles' 
import ExpandMoreIcon from '../../icons/svg/chevron-down'
import ExpandLessIcon from '../../icons/svg/chevron-up'
import { triggerEvent } from 'irmlib/common'

import { sidebarLeftPadding, appSidebarWidth, appOutsideFontSize, appSidebarItemTopPadding, appHeaderHeight, appHeaderbannerHeight } from './constants'
import { AppOutsideContainer } from './Common'
import { renderSideBarTreeViewSelector, triggerRenderSideBarTreeView, getClickedDashboard, setCurrentPath, getCurrentPath, getMultiTab } from '../../redux/global'
import NavRightSlider from '../../icons/svg/nav-right-slider'
import NavLeftSlider from '../../icons/svg/nav-left-slider'
// import { getSidebarRouteChoices } from '../../navigation/sidebarRoutes'
import { sideBarEvent, sideBarEventUrlClicked } from '../../common/constants'

import * as routePaths from '../../navigation/routePaths'
import TemplateManagement from '../../icons/svg/template'
import JobManagement from '../../icons/svg/job-management'
import ActivityManagement from '../../icons/svg/activity-management'
import Report from '../../icons/svg/report'

import { isAdmin, isSuperUser, isSupervisor } from '../../common/functions'
import { useTranslation } from 'react-i18next'
import EdgeAI from '../../icons/svg/edgeAI'
import Help from '../../icons/svg/help'
// import { getMultiTab } from '../../redux/global'

const treeItemSelectedFontWeight = 700
const chevronScale = 0.8
const iconTreeItemLeftMargin = '10px'

export const SidebarContainer = styled(AppOutsideContainer)`
  width: 100%;
  padding-left: ${sidebarLeftPadding};
  overflow-x: hidden ;
`

const LabelContainer = styled.div`
  font-size: ${appOutsideFontSize};
  display: flex;
  align-items: center;
  margin-top: 1px;
`

const ItemLabel = styled(Label)`
  white-space: pre-wrap;
  font-weight: 700;
`

const SideBarLink = styled(Link)`
  color: ${props => props.theme.palette.text.primary};
  text-decoration: none;
`

const IconTreeItemLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const ParentIconTreeItemLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const IconTreeItemIconContainer = styled.div`
  margin-top: -2px;
  margin-left: ${iconTreeItemLeftMargin};
`

const TreeViewContainer = styled.div`
  margin-top: -8px;
`

const IconTreeItemLabel = ({ node, isChildNode }) => {
  const theme = useTheme()
  return (
    <IconTreeItemLabelContainer style={{ marginLeft: isChildNode ? '13px' : undefined }}>
      {node.leftIcon}
      {node.leftIcon ? <XDivider width='6px' /> : null}
      <XDivider width='20px' />
      <LabelContainer>
        <ItemLabel noTranslate={true} style={{ fontWeight: node.linkUrl === getCurrentPath() ? treeItemSelectedFontWeight : 'normal', color: node.linkUrl === getCurrentPath() ? theme.palette.primary.main : '' }}>
          {node.label}
        </ItemLabel> 
      </LabelContainer>
    </IconTreeItemLabelContainer>
  )
}

IconTreeItemLabel.propTypes = {
  node: PropTypes.object,
  isChildNode: PropTypes.bool
}

const IconTreeItem = ({ node, theme, isChildNode, isSelected }) => {
  return (
    <SideBarLink theme={theme} to={node.linkUrl}>
      <IconTreeItemLabel node={node} isChildNode={isChildNode} isSelected={isSelected} />
    </SideBarLink>
  )
}

IconTreeItem.propTypes = {
  node: PropTypes.object,
  theme: PropTypes.object,
  isChildNode: PropTypes.bool,
  isSelected: PropTypes.bool
}

const ParentIconTreeItemLabel = ({ node, expanded }) =>
  <ParentIconTreeItemLabelContainer>
    <IconTreeItemLabel node={node} />
    <IconTreeItemIconContainer>
      {expanded ? <ExpandLessIcon scale={chevronScale} /> : <ExpandMoreIcon scale={chevronScale} />}
    </IconTreeItemIconContainer>
  </ParentIconTreeItemLabelContainer>

ParentIconTreeItemLabel.propTypes = {
  node: PropTypes.object,
  expanded: PropTypes.bool
}

const ParentTreeItem = ({ node, classes, childNodes, drawerOpen, handleDrawerOpen }) => {
  const [expanded, setExpanded] = useState(false)
  const onLabelClick = () => {
    setExpanded(!expanded)
    if (!expanded) {
      handleDrawerOpen()
    }
  }
  useEffect(() => {
    if (!drawerOpen) {
      setExpanded(false)
    }
  }, [drawerOpen])
  return (
    <TreeItem
      id={`dashboard-${node.label}`}
      onLabelClick={onLabelClick}
      nodeId={node.nodeId} 
      label={
        <ParentIconTreeItemLabel node={node} expanded={expanded} />
      } 
      classes={classes}
    >
      {childNodes}
    </TreeItem>
  )
}

ParentTreeItem.propTypes = {
  node: PropTypes.object,
  classes: PropTypes.object,
  childNodes: PropTypes.array,
  drawerOpen: PropTypes.bool,
  handleDrawerOpen: PropTypes.func
}

const SelectableTreeItem = ({ sidebarRouteChoices, node, isChildNode, classes, theme }) => {
  const onLabelClick = () => {
    // Turn off all selected nodes.
    sidebarRouteChoices.forEach(e => e.selected = false)
    // Select this node
    node.selected = true
    // store the path in globle state variable
    setCurrentPath(node.linkUrl)
    // If this is a child, turn on the parent
    if (node.isChild) {
      let index = sidebarRouteChoices.findIndex(e => e.nodeId === node.nodeId)
      if (index !== -1) {
        // Find the parent node.
        for (--index; index >= 0 && !sidebarRouteChoices[index].hasChildren;) { --index }
        if (index >= 0) {
          sidebarRouteChoices[index].selected = true
        }
      }
      // Trigger an event that a route was clicked.
      setTimeout(() => triggerEvent(sideBarEventUrlClicked, { detail: node.linkUrl }), 1)
    }
    // Force the tree view to render in order to pick up the nodes new selected values.
    triggerRenderSideBarTreeView()
  }
  return (
    <TreeItem
      id={`dashboard-${node.label}`}
      onLabelClick={onLabelClick}
      nodeId={node.nodeId} 
      label={
        <IconTreeItem theme={theme} node={node} isChildNode={isChildNode} isSelected={node.selected} />
      }
      classes={classes}
    />
  )
}

SelectableTreeItem.propTypes = {
  node: PropTypes.object,
  classes: PropTypes.object,
  isChildNode: PropTypes.bool,
  theme: PropTypes.object,
  sidebarRouteChoices: PropTypes.array
}

const DrawerIconContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 13px;
`

const DrawerOpenIcon = props =>
  <ClickableSvgIcon
    id='dashboard-DrawerOpenIcon'
    onClick={props.onClick}
  >
    <NavRightSlider scale={0.9} />
  </ClickableSvgIcon>

DrawerOpenIcon.propTypes = {
  onClick: PropTypes.func
}

const DrawerCloseIcon = props =>
  <ClickableSvgIcon
    id='dashboard-DrawerCloseIcon'
    onClick={props.onClick}
  >
    <NavLeftSlider scale={0.9} />
  </ClickableSvgIcon>

DrawerCloseIcon.propTypes = {
  onClick: PropTypes.func
}

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    maxWidth: parseInt(appSidebarWidth),
    minWidth: parseInt(appSidebarWidth),
    marginLeft: '-23px'
  }
})

const useTreeItemStyles = makeStyles((theme) => ({
  root: {
    paddingTop: appSidebarItemTopPadding,
  },
}))

/**
 * Sidebar is the side bar for the app.
 *
 * @component
 * @param {object} props component props
 * @returns {AppSidebarBase} AppSidebarBase component
 */
const AppSidebarBase = props => {
  const { t } = useTranslation()
  const prevClickedDashboard = useRef(getClickedDashboard())
  const theme = useTheme()
  const items = []
  const classes = useStyles()
  const itemClasses = useTreeItemStyles()
  // const sidebarRouteChoices = useSelector(getSidebarRouteChoices)
  useSelector(renderSideBarTreeViewSelector)
  const clickedDashboard = useSelector(getClickedDashboard)
  const [key, setKey] = useState(0)

  // when user direct visiting the web through url or first time visit
  const location = useLocation()
  if (!getCurrentPath()) {
    setCurrentPath(location.pathname)
  }
  const superUserSidebarRouteChoices = [
    { 
      label: t('routeTitles.jobManagement'),
      leftIcon: <JobManagement />,
      nodeId: '1',
      linkUrl: routePaths.todoComponent,
      hasChildren: true
    },
    // { 
    //   label: t('routeTitles.allJobs'),
    //   nodeId: '12',
    //   linkUrl: routePaths.AllJobs,
    //   isChild: true
    // },
    { 
      label: t('routeTitles.openJobs'),
      nodeId: '16',
      linkUrl: routePaths.AllOpenJobs,
      isChild: true
    },
    { 
      label: t('routeTitles.closedJobs'),
      nodeId: '17',
      linkUrl: routePaths.ClosedJobs,
      isChild: true
    },
    {
      label: t('routeTitles.myJobs'),
      nodeId: '13',
      linkUrl: routePaths.myJobs,
      isChild: true
    },
    {
      label: t('routeTitles.createNewJob'),
      nodeId: '11',
      linkUrl: routePaths.createJob,
      isChild: true
    },
    {
      label: t('routeTitles.qualityCheck'),
      nodeId: '14',
      linkUrl: routePaths.qualityCheck,
      isChild: true
    },
    {
      label: t('routeTitles.templateManagement'),
      leftIcon: <TemplateManagement />,
      nodeId: '3',
      hasChildren: true
    },
    {
      label: t('routeTitles.createTemplate'),
      nodeId: '31',
      linkUrl: routePaths.createTemplate,
      isChild: true
    },
    {
      // label: t('routeTitles.viewTemplateRoute',
      label: t('routeTitles.templateSearch'),
      nodeId: '32',
      linkUrl: routePaths.templateSearch, 
      isChild: true
    },
    { 
      label: t('routeTitles.reportsKeystrokesCapture'),
      leftIcon: <Report />,
      nodeId: '5',
      hasChildren: true
    },
    {
      label: t('routeTitles.customerReports'),
      nodeId: '51',
      linkUrl: routePaths.customerReports,
      isChild: true
    },
    {
      label: t('routeTitles.employeeReports'),
      nodeId: '52',
      linkUrl: routePaths.employeeReports,
      isChild: true
    }
  ]

  const adminSidebarRouteChoices = [
    { 
      label: t('routeTitles.jobManagement'),
      leftIcon: <JobManagement />,
      nodeId: '1',
      linkUrl: routePaths.todoComponent,
      hasChildren: true
    },
    // { 
    //   label: t('routeTitles.allJobs'),
    //   nodeId: '12',
    //   linkUrl: routePaths.AllJobs,
    //   isChild: true
    // },
    { 
      label: t('routeTitles.openJobs'),
      nodeId: '16',
      linkUrl: routePaths.AllOpenJobs,
      isChild: true
    },
    { 
      label: t('routeTitles.closedJobs'),
      nodeId: '17',
      linkUrl: routePaths.ClosedJobs,
      isChild: true
    },
    {
      label: t('routeTitles.myJobs'),
      nodeId: '13',
      linkUrl: routePaths.myJobs,
      isChild: true
    },
    {
      label: t('routeTitles.createNewJob'),
      nodeId: '11',
      linkUrl: routePaths.createJob,
      isChild: true
    },
    {
      label: t('routeTitles.qualityCheck'),
      nodeId: '14',
      linkUrl: routePaths.qualityCheck,
      isChild: true
    },
    {
      label: t('routeTitles.templateManagement'),
      leftIcon: <TemplateManagement />,
      nodeId: '3',
      hasChildren: true
    },
    {
      label: t('routeTitles.createTemplate'),
      nodeId: '31',
      linkUrl: routePaths.createTemplate,
      isChild: true
    },
    {
      // label: t('routeTitles.viewTemplateRoute',
      label: t('routeTitles.templateSearch'),
      nodeId: '32',
      linkUrl: routePaths.templateSearch, 
      isChild: true
    },
    { 
      label: t('routeTitles.reportsKeystrokesCapture'),
      leftIcon: <Report />,
      nodeId: '5',
      hasChildren: true
    },
    {
      label: t('routeTitles.customerReports'),
      nodeId: '51',
      linkUrl: routePaths.customerReports,
      isChild: true
    },
    {
      label: t('routeTitles.employeeReports'),
      nodeId: '52',
      linkUrl: routePaths.employeeReports,
      isChild: true
    }
  ]
  
  const supervisorSidebarRouteChoices = [
    { 
      label: t('routeTitles.jobManagement'),
      leftIcon: <JobManagement />,
      nodeId: '1',
      linkUrl: routePaths.todoComponent,
      hasChildren: true
    },
    // { 
    //   label: t('routeTitles.allOpenJobs'),
    //   nodeId: '11',
    //   linkUrl: routePaths.AllJobs,
    //   isChild: true
    // },
    { 
      label: t('routeTitles.openJobs'),
      nodeId: '16',
      linkUrl: routePaths.AllOpenJobs,
      isChild: true
    },
    { 
      label: t('routeTitles.closedJobs'),
      nodeId: '17',
      linkUrl: routePaths.ClosedJobs,
      isChild: true
    },
    { 
      label: t('routeTitles.myJobs'),
      nodeId: '12',
      linkUrl: routePaths.myJobs,
      isChild: true
    },
    {
      label: t('routeTitles.createNewJob'),
      nodeId: '14',
      linkUrl: routePaths.createJob,
      isChild: true
    },
    { 
      label: t('routeTitles.qualityCheck'),
      nodeId: '13',
      linkUrl: routePaths.qualityCheck,
      isChild: true
    },
    {
      label: t('routeTitles.templateManagement'),
      leftIcon: <TemplateManagement />,
      nodeId: '3',
      hasChildren: true
    },
    {
      // label: t('routeTitles.viewTemplateRoute',
      label: t('routeTitles.templateSearch'),
      nodeId: '32',
      linkUrl: routePaths.templateSearch, 
      isChild: true
    },
    { 
      label: t('routeTitles.reportsKeystrokesCapture'),
      leftIcon: <Report />,
      nodeId: '5',
      hasChildren: true
    },
    {
      label: t('routeTitles.customerReports'),
      nodeId: '51',
      linkUrl: routePaths.customerReports,
      isChild: true
    },
    {
      label: t('routeTitles.employeeReports'),
      nodeId: '52',
      linkUrl: routePaths.employeeReports,
      isChild: true
    },
  ]
  
  const userSidebarRouteChoices = [
    { 
      label: t('routeTitles.jobManagement'),
      leftIcon: <JobManagement />,
      nodeId: '1',
      hasChildren: true
    },
    { 
      label: t('routeTitles.myJobs'),
      nodeId: '11',
      linkUrl: routePaths.myJobs,
      isChild: true
    }
  ]

  let sidebarRouteChoices
  if (isAdmin()) {
    sidebarRouteChoices = adminSidebarRouteChoices
  } else if (isSupervisor()) {
    sidebarRouteChoices = supervisorSidebarRouteChoices
  } else if (isSuperUser()) {
    sidebarRouteChoices = superUserSidebarRouteChoices
  } else {
    sidebarRouteChoices = userSidebarRouteChoices
  }

  const additionalGivenPermissions = window.sessionStorage.getItem('additional-permissions-user')
  if (additionalGivenPermissions !== 'undefined' && additionalGivenPermissions !== undefined) {
    const additionalPermissionsForUser = JSON.parse(additionalGivenPermissions)
    const indexOfviewAllJobs = additionalPermissionsForUser?.map(object => object.value).indexOf('viewAllJobs')
    const indexOfQualityCheck = additionalPermissionsForUser?.map(object => object.value).indexOf('qualityCheck')
    const indexOfViewProductivityReport = additionalPermissionsForUser?.map(object => object.value).indexOf('viewProductivityReport')
    const indexOfSearchTemplate = additionalPermissionsForUser?.map(object => object.value).indexOf('searchTemplate')
    const indexOfCreateJob = additionalPermissionsForUser?.map(object => object.value).indexOf('createJob')
    const indexOfCreateTemplate = additionalPermissionsForUser?.map(object => object.value).indexOf('createTemplate')
    const indexOfSmartCatalog = additionalPermissionsForUser?.map(object => object.value).indexOf('smartCatalog')
    const indexOfSmartCatalogLocal = additionalPermissionsForUser?.map(object => object.value).indexOf('localAI')
    const indexofConfiguration = additionalPermissionsForUser?.map(object => object.value).indexOf('configuration')
    if (additionalPermissionsForUser?.[indexOfSmartCatalog]?.enabled) {
      superUserSidebarRouteChoices.push({
        label: t('routeTitles.smartCatalog'),
        leftIcon: <ActivityManagement />,
        nodeId: '7',
        hasChildren: true
      },
      {
        label: t('smartCatalog.smartCatalogDashBoard'),
        nodeId: '50',
        linkUrl: routePaths.SmartCatalogDashboard,
        isChild: true
      },
      {
        label: t('routeTitles.smartCatalogTemplateManagement'),
        nodeId: '54',
        linkUrl: routePaths.viewTemplate,
        isChild: true
      },
      {
        label: t('routeTitles.smartCatalogLockFieldManagement'),
        nodeId: '58',
        linkUrl: routePaths.ManageLockedFields,
        isChild: true
      },
      {
        label: t('routeTitles.addSmartcatalog'),
        nodeId: '55',
        linkUrl: routePaths.addActivity,
        isChild: true
      },
      {
        label: t('routeTitles.viewSmartCatalog'),
        nodeId: '56',
        linkUrl: routePaths.viewActivity,
        isChild: true
      },
      {
        label: t('routeTitles.addSmartcatalogImages'),
        nodeId: '57',
        linkUrl: routePaths.activityAddImages,
        isChild: true
      },
      {
        label: t('routeTitles.manageBoxTransmittals'),
        nodeId: '50',
        linkUrl: routePaths.viewBoxTransmittals,
        isChild: true
      },
      {
        label: t('routeTitles.manageCustExclusions'),
        nodeId: '59',
        linkUrl: routePaths.manageCustomerExclusions,
        isChild: true
      })
    }
    if (additionalPermissionsForUser?.[indexOfSmartCatalog]?.enabled) {
      adminSidebarRouteChoices.push({
        label: t('routeTitles.smartCatalog'),
        leftIcon: <ActivityManagement />,
        nodeId: '7',
        hasChildren: true
      },
      {
        label: t('smartCatalog.smartCatalogDashBoard'),
        nodeId: '50',
        linkUrl: routePaths.SmartCatalogDashboard,
        isChild: true
      },
      {
        label: t('routeTitles.addSmartcatalog'),
        nodeId: '55',
        linkUrl: routePaths.addActivity,
        isChild: true
      },
      {
        label: t('routeTitles.viewSmartCatalog'),
        nodeId: '56',
        linkUrl: routePaths.viewActivity,
        isChild: true
      },
      {
        label: t('routeTitles.addSmartcatalogImages'),
        nodeId: '57',
        linkUrl: routePaths.activityAddImages,
        isChild: true
      },
      {
        label: t('routeTitles.manageCustExclusions'),
        nodeId: '59',
        linkUrl: routePaths.manageCustomerExclusions,
        isChild: true
      })
    }

    if (additionalPermissionsForUser?.[indexOfCreateTemplate]?.enabled) {
      supervisorSidebarRouteChoices.splice(6, 0, {
        label: t('routeTitles.createTemplate'),
        nodeId: '31',
        linkUrl: routePaths.createTemplate,
        isChild: true
      })
    }
    if (additionalPermissionsForUser?.[indexOfviewAllJobs]?.enabled) {
      userSidebarRouteChoices.splice(1, 0, 
        { 
          label: t('routeTitles.openJobs'),
          nodeId: '16',
          linkUrl: routePaths.AllOpenJobs,
          isChild: true
        },
        { 
          label: t('routeTitles.closedJobs'),
          nodeId: '17',
          linkUrl: routePaths.ClosedJobs,
          isChild: true
        },
      )   
    }
    if (additionalPermissionsForUser?.[indexOfCreateJob]?.enabled) {
      userSidebarRouteChoices.push({
        label: t('routeTitles.createNewJob'),
        nodeId: '13',
        linkUrl: routePaths.createJob,
        isChild: true
      })   
    }
    if (additionalPermissionsForUser?.[indexOfQualityCheck]?.enabled) {
      userSidebarRouteChoices.push({
        label: t('routeTitles.qualityCheck'),
        nodeId: '14',
        linkUrl: routePaths.qualityCheck,
        isChild: true
      })
    }
    if (additionalPermissionsForUser?.[indexOfSearchTemplate]?.enabled) {
      userSidebarRouteChoices.push({
        label: t('routeTitles.templateManagement'),
        leftIcon: <TemplateManagement />,
        nodeId: '3',
        hasChildren: true
      }, {
        label: t('routeTitles.templateSearch'),
        nodeId: '31',
        linkUrl: routePaths.templateSearch, 
        isChild: true
      })
    }
    if (additionalPermissionsForUser?.[indexOfViewProductivityReport]?.enabled) {
      userSidebarRouteChoices.push({ 
        label: t('routeTitles.reportsKeystrokesCapture'),
        leftIcon: <Report />,
        nodeId: '5',
        hasChildren: true
      },
      {
        label: t('routeTitles.customerReports'),
        nodeId: '51',
        linkUrl: routePaths.customerReports,
        isChild: true
      },
      {
        label: t('routeTitles.employeeReports'),
        nodeId: '52',
        linkUrl: routePaths.employeeReports,
        isChild: true
      })
    }
    if (additionalPermissionsForUser?.[indexOfSmartCatalog]?.enabled) {
      supervisorSidebarRouteChoices.push({
        label: t('routeTitles.smartCatalog'),
        leftIcon: <ActivityManagement />,
        nodeId: '7',
        hasChildren: true
      },
      {
        label: t('routeTitles.viewSmartCatalog'),
        nodeId: '56',
        linkUrl: routePaths.viewActivity,
        isChild: true
      },
      {
        label: t('routeTitles.addSmartcatalogImages'),
        nodeId: '57',
        linkUrl: routePaths.activityAddImages,
        isChild: true
      },
      {
        label: t('routeTitles.manageCustExclusions'),
        nodeId: '59',
        linkUrl: routePaths.manageCustomerExclusions,
        isChild: true
      })
    }
    if (additionalPermissionsForUser?.[indexOfSmartCatalog]?.enabled) {
      userSidebarRouteChoices.push({
        label: t('routeTitles.smartCatalog'),
        leftIcon: <ActivityManagement />,
        nodeId: '7',
        hasChildren: true
      },
      {
        label: t('routeTitles.viewSmartCatalog'),
        nodeId: '56',
        linkUrl: routePaths.viewActivity,
        isChild: true
      },
      {
        label: t('routeTitles.addSmartcatalogImages'),
        nodeId: '57',
        linkUrl: routePaths.activityAddImages,
        isChild: true
      },
      {
        label: t('routeTitles.manageCustExclusions'),
        nodeId: '59',
        linkUrl: routePaths.manageCustomerExclusions,
        isChild: true
      })
    }

    if (additionalPermissionsForUser?.[indexofConfiguration]?.enabled) {
      superUserSidebarRouteChoices.push({
        label: t('routeTitles.edgeAiConfiguration'),
        leftIcon: <Help />,
        nodeId: '10',
        hasChildren: true
      },
      {
        label: t('routeTitles.edgeCountrySettings'),
        nodeId: '58',
        linkUrl: routePaths.edgeCountrySettings,
        isChild: true
      },
      {
        label: t('routeTitles.edgeDeviceMgmt'),
        nodeId: '59',
        linkUrl: routePaths.edgeDeviceManagement,
        isChild: true
      })
    }
  }

    

  // If the dashboard is clicked, this turns off all the selected items.
  if (clickedDashboard !== prevClickedDashboard.current) {
    for (let i = 0; i < sidebarRouteChoices.length; ++i) {
      sidebarRouteChoices[i].selected = false
    }
  }
  prevClickedDashboard.current = clickedDashboard

  const handleDrawerClose = () => {
    sidebarRouteChoices.forEach(e => e.selected = false)
    props.handleDrawerClose()
    setKey(key => key + 1)
  }

  const makeTreeItem = (sidebarRouteChoices, node, classes, isChildNode) =>
    <SelectableTreeItem
      key={node.nodeId}
      theme={theme}
      sidebarRouteChoices={sidebarRouteChoices}
      node={node} 
      classes={classes}
      isChildNode={isChildNode}
      noTranslate={true}
    />

  const buildTreeItems = (classes, sidebarRouteChoices, drawerOpen, handleDrawerOpen) => {
    for (let i = 0; i < sidebarRouteChoices.length;) {
      const node = sidebarRouteChoices[i]
      if (node.hasChildren) {
        ++i
        const childNodes = []
        while (i < sidebarRouteChoices.length && sidebarRouteChoices[i].isChild) {
          childNodes.push(makeTreeItem(sidebarRouteChoices, sidebarRouteChoices[i++], classes, true))
        }
        items.push(
          <ParentTreeItem
            key={node.nodeId}
            node={node} 
            classes={classes}
            childNodes={childNodes}
            drawerOpen={drawerOpen}
            handleDrawerOpen={handleDrawerOpen}
            noTranslate={true}
          />
        )
      } else {
        items.push(makeTreeItem(sidebarRouteChoices, node, classes, false))
        ++i
      }
    }
    return items
  }
  return (
    <SidebarContainer key={key} theme={theme}>
      <DrawerIconContainer>
        {props.open ? <DrawerCloseIcon onClick={handleDrawerClose} /> : <DrawerOpenIcon onClick={props.handleDrawerOpen} />}
      </DrawerIconContainer>
      <TreeViewContainer>
        <TreeView
          className={classes.root}
      >
          {buildTreeItems(itemClasses, sidebarRouteChoices, props.open, props.handleDrawerOpen)}
        </TreeView>
      </TreeViewContainer>
    </SidebarContainer>
  )
}

AppSidebarBase.propTypes = {
  open: PropTypes.bool,
  handleDrawerClose: PropTypes.func,
  handleDrawerOpen: PropTypes.func,
}

const AppSidebar = props => {
  return (
    <Drawer
      defaultOpen={false}
      width={appSidebarWidth}
      top={getMultiTab() ? appHeaderbannerHeight : appHeaderHeight }
      drawerContent={props => {
        const handleDrawerClose = () => {
          props.handleDrawerClose()
          setTimeout(() => triggerEvent(sideBarEvent), 1)
        }
        const handleDrawerOpen = () => {
          props.handleDrawerOpen()
          setTimeout(() => triggerEvent(sideBarEvent), 1)
        }
        // eslint-disable-next-line react/prop-types
        return <AppSidebarBase handleDrawerClose={handleDrawerClose} handleDrawerOpen={handleDrawerOpen} open={props.open} /> 
      }
      }
    />
  )
}

AppSidebar.propTypes = {
  handleDrawerOpen: PropTypes.func,
  handleDrawerClose: PropTypes.func,
}

export default AppSidebar